import { Temporal } from '@js-temporal/polyfill';
import postalZones from './usPostalTimezones.js';

export const defaultEventTimezone = 'America/Chicago';
export function validTimezone(value) {
  if (typeof value !== 'string' || (value !== 'UTC' && !value.includes('/'))) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: value }); return true; } catch { return false; }
}
export function addressTimezone(address) {
  if (!address) return null;
  const text = typeof address === 'string' ? address : [address.address_line1, address.city, address.state, address.zip_code].filter(Boolean).join(' ');
  const zip = typeof address === 'object' && address.zip_code
    ? String(address.zip_code).trim().match(/^(\d{5})(?:-\d{4})?$/)?.[1]
    : text.match(/\b[A-Z]{2}\s+(\d{5})(?:-\d{4})?\s*(?:USA|US)?\s*$/i)?.[1];
  if (!zip) return null;
  const state = typeof address === 'object' ? String(address.state || '').trim().toUpperCase() : text.match(/\b([A-Z]{2})\s+\d{5}(?:-\d{4})?\s*(?:USA|US)?\s*$/i)?.[1]?.toUpperCase();
  return postalZones.find(([start, end, region]) => Number(zip) >= start && Number(zip) <= end && (!state || state === region))?.[3] || null;
}
export function viewerTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}
export function viewerToday() {
  return Temporal.Now.plainDateISO(viewerTimezone()).toString();
}
export function shiftEventDate(date, days) {
  return Temporal.PlainDate.from(date).add({ days }).toString();
}
export function eventInstant(date, time, timezone = defaultEventTimezone) {
  if (!date || !time) return null;
  try {
    return Temporal.PlainDateTime.from(`${date}T${time}`).toZonedDateTime(timezone || defaultEventTimezone, { disambiguation: 'compatible' }).toInstant();
  } catch { return null; }
}
export function localEventParts(record, time, targetTimezone = viewerTimezone()) {
  let date = record?.match_date || record?.event_date || record?.date || '';
  const clock = time === undefined ? record?.match_time || record?.start_time || record?.warmup_time : time;
  if (date && time && record?.end_time === time && record?.start_time && time < record.start_time) date = Temporal.PlainDate.from(date).add({ days: 1 }).toString();
  const instant = eventInstant(date, clock, record?.timezone || defaultEventTimezone);
  if (!instant) return { date, time: clock || '', timezone: targetTimezone };
  const local = instant.toZonedDateTimeISO(targetTimezone);
  return { date: local.toPlainDate().toString(), time: local.toPlainTime().toString().slice(0, 8), timezone: targetTimezone };
}
export function localEventDate(record, targetTimezone = viewerTimezone()) {
  return localEventParts(record, undefined, targetTimezone).date;
}
export function localizePlayerScheduleItem(item, targetTimezone = viewerTimezone(), inherited = {}) {
  if (!item) return item;
  const source = { ...inherited, ...item, date: item.date || inherited.date, timezone: item.timezone || inherited.timezone };
  const clock = item.time || item.startTime || item.warmupTime || source.time || source.startTime || source.warmupTime;
  source.start_time = clock;
  source.end_time = item.endTime;
  const result = { ...item, date: localEventParts(source, clock, targetTimezone).date, timezone: targetTimezone };
  for (const field of ['time', 'startTime', 'endTime', 'warmupTime', 'arrivalTime']) {
    if (item[field]) result[field] = localEventParts(source, item[field], targetTimezone).time;
  }
  if (item.playerAssignment) result.playerAssignment = localizePlayerScheduleItem(item.playerAssignment, targetTimezone, source);
  if (item.lineup) result.lineup = { ...item.lineup, lines: item.lineup.lines.map(line => localizePlayerScheduleItem(line, targetTimezone, source)) };
  return result;
}
export function formatEventTime(record, time, format = '12h', targetTimezone = viewerTimezone()) {
  if (!time) return '';
  const local = localEventParts(record, time, targetTimezone);
  const [hour, minute] = local.time.split(':').map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return '';
  if (format === '24h') return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
}
